/**
 * MapWrapper - Crash-isolated wrapper for Leaflet maps
 *
 * Encapsulates the map in an error boundary that:
 * - Catches all map-related crashes
 * - Provides a friendly fallback UI
 * - Allows retry without page reload
 * - Resets on key change (tab switching)
 */
import React, { Component } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, RefreshCw, ZoomIn, ZoomOut, Locate, AlertTriangle } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

/**
 * Error Boundary that isolates map crashes
 */
class MapErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Map Error Caught:', error);
    console.error('Error Info:', errorInfo);
    this.setState({ errorInfo });
  }

  // Reset error state when mapKey changes (e.g., tab switch)
  componentDidUpdate(prevProps) {
    if (prevProps.mapKey !== this.props.mapKey && this.state.hasError) {
      this.setState({ hasError: false, error: null, errorInfo: null });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-center p-8 max-w-md">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Erreur d'affichage de la carte
            </h3>
            <p className="text-gray-500 mb-4">
              La carte a rencontré un problème. Cela n'affecte pas le reste de l'application.
            </p>
            <button
              onClick={this.handleRetry}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Réessayer
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Safe map controls - checks map availability before operations
 * Uses onMouseDown to prevent Leaflet event interference
 */
function SafeMapControls({ onRefresh, isRefreshing }) {
  const map = useMap();

  if (!map) return null;

  const handleZoomIn = (e) => {
    e.stopPropagation();
    e.preventDefault();
    try { map.zoomIn(); } catch (err) { console.warn('Zoom in failed:', err); }
  };

  const handleZoomOut = (e) => {
    e.stopPropagation();
    e.preventDefault();
    try { map.zoomOut(); } catch (err) { console.warn('Zoom out failed:', err); }
  };

  const handleLocate = (e) => {
    e.stopPropagation();
    e.preventDefault();
    try { map.locate({ setView: true, maxZoom: 14 }); } catch (err) { console.warn('Locate failed:', err); }
  };

  const handleRefresh = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onRefresh) onRefresh();
  };

  // Stop all Leaflet event propagation on the control container
  const stopLeafletPropagation = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      className="absolute top-4 right-4 z-[1000] flex flex-col gap-2"
      onMouseDown={stopLeafletPropagation}
      onDoubleClick={stopLeafletPropagation}
    >
      <button
        type="button"
        onClick={handleZoomIn}
        className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-100 cursor-pointer"
        title="Zoom avant"
      >
        <ZoomIn className="w-5 h-5 text-gray-700" />
      </button>
      <button
        type="button"
        onClick={handleZoomOut}
        className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-100 cursor-pointer"
        title="Zoom arrière"
      >
        <ZoomOut className="w-5 h-5 text-gray-700" />
      </button>
      <button
        type="button"
        onClick={handleLocate}
        className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-100 cursor-pointer"
        title="Ma position"
      >
        <Locate className="w-5 h-5 text-gray-700" />
      </button>
      {onRefresh && (
        <button
          type="button"
          onClick={handleRefresh}
          className={`p-2 bg-white rounded-lg shadow-md hover:bg-gray-100 cursor-pointer ${isRefreshing ? 'animate-spin' : ''}`}
          title="Actualiser"
          disabled={isRefreshing}
        >
          <RefreshCw className="w-5 h-5 text-gray-700" />
        </button>
      )}
    </div>
  );
}

/**
 * Safe fit bounds - handles errors gracefully
 */
function SafeFitBounds({ positions }) {
  const map = useMap();

  React.useEffect(() => {
    if (!map || !positions || positions.length === 0) return;

    try {
      const bounds = L.latLngBounds(positions);
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    } catch (e) {
      console.warn('FitBounds failed:', e);
    }
  }, [positions, map]);

  return null;
}

/**
 * Main MapWrapper component
 *
 * Usage:
 * <MapWrapper
 *   mapKey="unique-key"
 *   markers={[{ id, lat, lng, popup: <Component /> }]}
 *   center={[lat, lng]}
 *   zoom={6}
 *   onRefresh={() => refetch()}
 *   isRefreshing={isLoading}
 *   height="500px"
 *   emptyMessage="Aucun élément géolocalisé"
 * />
 */
function MapWrapper({
  mapKey = 'map',
  markers = [],
  center = [46.603354, 1.888334], // France center
  zoom = 6,
  onRefresh,
  isRefreshing = false,
  height = 'calc(100vh - 320px)',
  emptyMessage = 'Aucun élément géolocalisé',
  emptyDescription = 'Ajoutez des coordonnées GPS pour voir les éléments sur la carte.',
  showControls = true,
  showFitBounds = true,
  className = '',
  children
}) {
  // Filter valid markers
  const validMarkers = React.useMemo(() => {
    return markers.filter(m => {
      const lat = parseFloat(m.lat);
      const lng = parseFloat(m.lng);
      return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
    });
  }, [markers]);

  // Get positions for bounds
  const positions = React.useMemo(() => {
    return validMarkers.map(m => [parseFloat(m.lat), parseFloat(m.lng)]);
  }, [validMarkers]);

  // Compute center from markers if available
  const computedCenter = positions.length > 0 ? positions[0] : center;

  // Empty state
  if (validMarkers.length === 0) {
    return (
      <div
        className={`bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="text-center p-8">
          <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">{emptyMessage}</h3>
          <p className="text-gray-500 max-w-md">{emptyDescription}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden relative ${className}`}
      style={{ height }}
    >
      <MapErrorBoundary mapKey={mapKey}>
        <MapContainer
          key={mapKey}
          center={computedCenter}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
          className="z-0"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {showFitBounds && positions.length > 0 && <SafeFitBounds positions={positions} />}
          {showControls && <SafeMapControls onRefresh={onRefresh} isRefreshing={isRefreshing} />}

          {validMarkers.map(marker => (
            <Marker
              key={marker.id}
              position={[parseFloat(marker.lat), parseFloat(marker.lng)]}
              {...(marker.icon ? { icon: marker.icon } : {})}
            >
              {marker.popup && <Popup maxWidth={360} minWidth={200} autoPan={false}>{marker.popup}</Popup>}
            </Marker>
          ))}

          {children}
        </MapContainer>
      </MapErrorBoundary>
    </div>
  );
}

// Export utilities for custom icons
export { L, MapErrorBoundary, SafeMapControls, SafeFitBounds };
export default MapWrapper;
